import { Suspense, lazy } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import {
  DebugProvider,
  PubSubProvider,
  SnackbarController,
  TrackingProvider,
} from "@huub-web/ui";
import { GTM, createGTMTracker } from "@huub-web/shared";

import i18n from "./i18n";

import { RouteChangeTracker } from "./components/RouteChangeTracker/RouteChangeTracker";
import { AppController } from "./components/AppController/AppController";
import { AuthProvider } from "./components/AuthProvider/AuthProvider";
import { AppSettingsProvider } from "./components/AppSettingsProvider/AppSettingsProvider";
import { UIProvider } from "./components/UIProvider/UIProvider";
import { WebSocketProvider } from "./components/WebSocketProvider/WebSocketProvider";
import { APIProvider } from "./components/APIProvider/APIProvider";
import { AUTH_MODE } from "./config/authConfig";

const AuthLoginPage = lazy(() =>
  import("./components/AuthLoginPage/AuthLoginPage").then((m) => ({
    default: m.AuthLoginPage,
  })),
);
const AuthRegisterPage = lazy(() =>
  import("./components/AuthRegisterPage/AuthRegisterPage").then((m) => ({
    default: m.AuthRegisterPage,
  })),
);
const AuthCallbackPage = lazy(() =>
  import("./components/AuthCallbackPage/AuthCallbackPage").then((m) => ({
    default: m.AuthCallbackPage,
  })),
);

const WEBSOCKET_BASE_ENDPOINT = import.meta.env.VITE_WEBSOCKET_BASE_ENDPOINT;
const API_BASE_ENDPOINT = import.meta.env.VITE_API_BASE_ENDPOINT;

const GTM_CONTAINER_ID = import.meta.env.VITE_GTM_CONTAINER_ID;

const OAUTH_LOGIN_URL = import.meta.env.VITE_OAUTH_LOGIN_URL;
const OAUTH_LOGOUT_ENDPOINT = import.meta.env.VITE_OAUTH_LOGOUT_ENDPOINT;
const OAUTH_REFRESH_ENDPOINT = import.meta.env.VITE_OAUTH_REFRESH_ENDPOINT;
const AUTH_COOKIE_DOMAIN = import.meta.env.VITE_AUTH_COOKIE_DOMAIN;
const AUTH_COOKIE_NAME = import.meta.env.VITE_AUTH_COOKIE_NAME;

const UI_DEBUG_ENABLED = import.meta.env?.VITE_UI_DEBUG_ENABLED === "true";
const UI_RESPONSE_FEEDBACK_ENABLED =
  import.meta.env?.VITE_UI_RESPONSE_FEEDBACK_ENABLED === "true";

const TTS_ENABLED = import.meta.env.VITE_TTS_ENABLED === "true";

if (!WEBSOCKET_BASE_ENDPOINT) {
  throw new Error("VITE_WEBSOCKET_BASE_ENDPOINT is not defined");
}
if (!API_BASE_ENDPOINT) {
  throw new Error("VITE_API_BASE_ENDPOINT is not defined");
}
if (AUTH_MODE === "external" && !OAUTH_LOGIN_URL) {
  throw new Error("VITE_OAUTH_LOGIN_URL is not defined");
}
if (!OAUTH_LOGOUT_ENDPOINT) {
  throw new Error("VITE_OAUTH_LOGOUT_ENDPOINT is not defined");
}
if (!OAUTH_REFRESH_ENDPOINT) {
  throw new Error("VITE_OAUTH_REFRESH_ENDPOINT is not defined");
}
if (!AUTH_COOKIE_NAME) {
  throw new Error("VITE_AUTH_COOKIE_NAME is not defined");
}

/**
 * The main entry point of the application.
 */
function App() {
  return (
    <DebugProvider debug={UI_DEBUG_ENABLED}>
      <GTM id={GTM_CONTAINER_ID} />

      <BrowserRouter>
        <TrackingProvider
          trackers={{
            gtm: createGTMTracker(),
            // insights: createInsightsTracker({
            //   endpoint:
            //     import.meta.env.VITE_INSIGHTS_ENDPOINT || "/api/insights",
            //   debug: import.meta.env.DEV,
            //   batchEvents: true,
            // }),
          }}
        >
          <RouteChangeTracker />
          <PubSubProvider>
            <I18nextProvider i18n={i18n}>
              <Suspense fallback={null}>
                <AppSettingsProvider
                  debug={UI_DEBUG_ENABLED}
                  responseFeedback={UI_RESPONSE_FEEDBACK_ENABLED}
                >
                  <UIProvider>
                    <Routes>
                      {AUTH_MODE === "internal" && (
                        <>
                          <Route
                            path="/auth/login"
                            element={<AuthLoginPage />}
                          />
                          <Route
                            path="/auth/register"
                            element={<AuthRegisterPage />}
                          />
                          <Route
                            path="/auth/callback"
                            element={<AuthCallbackPage />}
                          />
                        </>
                      )}
                      <Route
                        path="/*"
                        element={
                          <AuthProvider
                            loginURL={OAUTH_LOGIN_URL ?? ""}
                            logoutEndpoint={OAUTH_LOGOUT_ENDPOINT}
                            refreshEndpoint={OAUTH_REFRESH_ENDPOINT}
                            authCookieDomain={AUTH_COOKIE_DOMAIN}
                            authCookieName={AUTH_COOKIE_NAME}
                          >
                            <APIProvider baseEndpoint={API_BASE_ENDPOINT}>
                              <WebSocketProvider
                                baseEndpoint={WEBSOCKET_BASE_ENDPOINT}
                                ttsEnabled={TTS_ENABLED}
                              >
                                <SnackbarController />
                                <AppController ttsEnabled={TTS_ENABLED} />
                              </WebSocketProvider>
                            </APIProvider>
                          </AuthProvider>
                        }
                      />
                    </Routes>
                  </UIProvider>
                </AppSettingsProvider>
              </Suspense>
            </I18nextProvider>
          </PubSubProvider>
        </TrackingProvider>
      </BrowserRouter>
    </DebugProvider>
  );
}

export default App;
